<template>
    <section class="payment-overview-summary">
        <div class="payment-overview-summary__heading">
            <span class="payment-overview-summary__heading-text">{{ structuredHeading }}</span>
            <span v-if="structuredDetails.length" class="payment-overview-summary__details">
                <template v-for="(detail, index) in structuredDetails">
                    <span v-if="index" :key="`${detail.key}-separator`"> · </span>
                    <strong v-if="detail.emphasized" :key="detail.key">{{ detail.text }}</strong>
                    <span v-else :key="detail.key">{{ detail.text }}</span>
                </template>
            </span>
        </div>
        <dl class="payment-overview-summary__totals">
            <div v-for="item in items" :key="item.label" class="payment-overview-summary__total">
                <dt>
                    <abbr
                        v-if="item.accountingData"
                        :aria-label="accountingTooltip(item)"
                        class="hint--top-left hint--large"
                        tabindex="0"
                    >{{ item.label }}*</abbr>
                    <template v-else>
                        {{ item.label }}
                    </template>
                </dt>
                <dd>{{ formatAmount(item.value) }}</dd>
            </div>
        </dl>
    </section>
</template>

<script>
import ReportingDateRanges from '../../mixins/Inputs/ReportingDateRanges';

export default {
    mixins: [ReportingDateRanges],

    props: {
        activeFilters: {
            type: Array,
            default: () => [],
        },

        accountingEnabled: {
            type: Boolean,
            required: true,
        },

        filters: {
            type: Array,
            default: () => [],
        },

        rows: {
            type: Array,
            default: () => [],
        },

        searchQuery: {
            type: String,
            default: '',
        },
    },

    computed: {
        amountPaid() {
            return this.sumAvailable('amount_paid');
        },

        amountPaidIncomplete() {
            return this.hasMissingValue('amount_paid');
        },

        amountRemaining() {
            return this.sumAvailable('amount_remaining');
        },

        amountRemainingIncomplete() {
            return this.hasMissingValue('amount_remaining');
        },

        manuallyPaidAmount() {
            return this.sumByPaymentStatus(true);
        },

        manuallyRemainingAmount() {
            return this.sumByPaymentStatus(false);
        },

        totalAmount() {
            return this.sum('total_amount');
        },

        instituteName() {
            const instituteId = String(this.$store.state.core.instituteId || '');
            const institute = window.frm.state.institutes.find(option => String(option.value) === instituteId) ||
                window.frm.state.institutes[0];

            return institute ? institute.label : '';
        },

        invoiceType() {
            return this.selectedInvoiceType || this.$t('financial.payment_summary.all_invoices');
        },

        selectedInvoiceType() {
            return this.selectedOptionLabel('debtor_type');
        },

        paymentStatus() {
            return this.selectedOptionLabel('is_paid');
        },

        dateRange() {
            if (!this.dateRangeValues) {
                return '';
            }

            return this.$t('financial.payment_summary.date_range', this.dateRangeValues);
        },

        selectedReportingDateRangeLabel() {
            const range = this.findReportingDateRange(this.activeFilterValue('date'));

            return range ? range.label : '';
        },

        dateRangeValues() {
            const value = this.activeFilterValue('date');

            if (!Array.isArray(value) || value.length !== 2 || !value[0] || !value[1]) {
                return null;
            }

            return {
                from: this.formatDate(value[0]),
                to: this.formatDate(value[1]),
            };
        },

        normalizedSearchQuery() {
            return typeof this.searchQuery === 'string' ? this.searchQuery.trim() : '';
        },

        structuredDetails() {
            return [
                { emphasized: true, key: 'period', text: this.selectedReportingDateRangeLabel },
                { key: 'date-range', text: this.dateRange },
                { key: 'payment-status', text: this.paymentStatus },
                this.normalizedSearchQuery
                    ? {
                        key: 'search-query',
                        text: this.$t('financial.payment_summary.search_query', { query: this.normalizedSearchQuery }),
                    }
                    : null,
            ].filter(detail => detail && detail.text);
        },

        structuredHeading() {
            return this.$t('financial.payment_summary.structured_heading', {
                type: this.invoiceType,
                institute: this.instituteName,
            });
        },

        items() {
            const received = {
                accountingData: this.accountingEnabled,
                incomplete: this.accountingEnabled && this.amountPaidIncomplete && this.amountPaid !== null,
                label: this.$t('financial.payment_summary.received'),
                value: this.accountingEnabled ? this.amountPaid : this.manuallyPaidAmount,
            };
            const remaining = {
                accountingData: this.accountingEnabled,
                incomplete: this.accountingEnabled && this.amountRemainingIncomplete && this.amountRemaining !== null,
                label: this.$t('financial.payment_summary.amount_to_receive'),
                value: this.accountingEnabled ? this.amountRemaining : this.manuallyRemainingAmount,
            };
            const total = {
                accountingData: false,
                incomplete: false,
                label: this.$t('financial.payment_summary.total_invoice_amount'),
                value: this.totalAmount,
            };

            return [received, remaining, total];
        },
    },

    methods: {
        accountingTooltip(item) {
            const disclaimer = this.$t('financial.payment_summary.accounting_disclaimer');

            return item.incomplete
                ? `${disclaimer} ${this.$t('financial.payment_summary.incomplete_total')}`
                : disclaimer;
        },

        activeFilterValue(attribute) {
            const filter = this.activeFilters.find(item => item.attribute === attribute);

            return filter ? filter.value : null;
        },

        formatAmount(amount) {
            return amount === null ? '—' : this.$c(amount);
        },

        formatDate(value) {
            const date = this.parseDate(value);

            return date ? this.$d(date, 'date') : '';
        },

        parseDate(value) {
            const date = value instanceof Date ? value : new Date(value);

            return Number.isNaN(date.getTime()) ? null : date;
        },

        hasMissingValue(attribute) {
            return this.rows.some(row => row[attribute] === null || row[attribute] === undefined);
        },

        sum(attribute) {
            if (this.hasMissingValue(attribute)) {
                return null;
            }

            return this.rows.reduce((total, row) => total + Number(row[attribute]), 0);
        },

        sumAvailable(attribute) {
            const values = this.rows
                .map(row => row[attribute])
                .filter(value => value !== null && value !== undefined);

            if (this.rows.length && !values.length) {
                return null;
            }

            return values.reduce((total, value) => total + Number(value), 0);
        },

        sumByPaymentStatus(isPaid) {
            if (this.rows.some(row => typeof row.is_paid !== 'boolean')) {
                return null;
            }

            return this.rows
                .filter(row => row.is_paid === isPaid)
                .reduce((total, row) => total + Number(row.total_amount), 0);
        },

        selectedOptionLabel(attribute) {
            const value = this.activeFilterValue(attribute);
            const filter = this.filters.find(item => item.attribute === attribute);

            if (!filter || !value || !Array.isArray(filter.options)) {
                return '';
            }

            const option = filter.options.find(item => String(item.value) === String(value));

            return option ? option.label : '';
        },
    },
};
</script>
